import React, {useEffect, useMemo, useRef, useState} from "react";
import {useProject} from "../../../providers/ClientProjectProvider";
import Modal from "../../../components/Modal";
import LoadingBanner from "../../../components/LoadingBanner";
import ErrorBanner from "../../../components/ErrorBanner";
import Form from "../../../components/form/Form";
import User from "../../../models/User";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {useAuth} from "../../../providers/AuthProvider";
import ProjectGroup from "../../../models/ProjectGroup";
import InfoTooltip from "../../../components/InfoTooltip"
import {useSearchParams} from "react-router-dom";
import {useAPI} from "../../../config/API";


const ShareForm = ({position, users = [], ...props}) => {
    const {project} = useProject();
    const {auth} = useAuth();
    const [groupsSelected, setGroupsSelected] = useState([]);
    const shareFormRef = useRef(null);
    const [formModel, setFormModel] = useState({
        recipient_ids: [],
        recipient_emails: [],

        talent_user_ids: [],

        subject: 'Feedback on Wripple Talent',
        intro_message: `Hi,\nHere are some great candidates that I found on Wripple. You can click the links to learn more about the candidates. Let me know what you think!`,

        include_bio_match_score: true,
        include_linkedin: true,
        include_resume: true,
        include_profile_link: true
    });

    const onChangeModel = (model) => {
        // console.log('onChangeShareModel', model);
        return setFormModel(model);
    }

    const sharableUsers = useMemo(
        () => {

            let options = TB.filter(users, ['is_rejected', false, 'is_withdrawn', false]);
            let [invited, other] = TB.partition(options,'is_shortlisted');

            if (invited.length && other.length) {
                options = [
                    {name: 'Shortlisted', options:invited},
                    {name: 'Other Talent', options:other}
                ]
            }
            return options;
        },
        [users]
    );
    const toggleOptions = useMemo(() => {
        let options = [
            {label: 'Shortlisted', value: 'is_shortlisted'},
            {label: 'Interested', value: 'is_interested'},
            {label: 'Liked', value: 'is_liked'},
            {label: 'Favorited', value: 'is_favorite'},
        ];
        if (sharableUsers.length) {
            let talent = [...sharableUsers]
            if (TB.find(sharableUsers, 'options')) {
                talent = TB.flatten(TB.map(sharableUsers, 'options'))
            }
            options = TB.map(options, option => {
                return {
                    ...option,
                    // count: TB.filter(talent, [option.value, true]).length
                    count: TB.filter(talent, t => {
                        if (option.value === 'is_interested' && project.isBYO()) {
                            return !!t.has_confirmed_payout;
                        }
                        return !!t[option.value];
                    }).length,
                }
            })
            options = TB.filter(options, ({count}) => count > 0);
        }
        return options;
    }, [sharableUsers]);
    useEffect(() => {

        if (sharableUsers.length) {
            setGroupsSelected(['is_invited'])
        }
    }, [sharableUsers]);

    const {
        data: recipients,
        isLoading: isLoadingRecipients,
        error,
        refetch,
    } = useAPI(['fetchRecipientUsers', {group: project?.client_project_group}]
    )

    const suggestions = useMemo(() => {
        if (recipients?.length) {

            return TB.map(TB.reject(recipients, ['id', auth.id]), recipient => {
                let clientUser = User.make({...recipient});
                let option = {
                    format: (
                        <span className="text-black">{clientUser.fullName()} <small className="text-bold">{`<${clientUser.email}>`}</small></span>
                    ),
                    label: clientUser.fullName() + " " + clientUser.email,
                    value: clientUser.id,
                    disabled: false
                }

                if (!clientUser.canViewTalentForProject(project)) {
                    option.disabled = true;
                    option.label = clientUser.fullName();
                    option.format = (
                        <span>{clientUser.fullName()}&nbsp; <small><i className="text-danger" style={{opacity: 0.85}}>Missing
                            permission to view talent</i></small></span>
                    )
                }
                return option
            });
        }
        return []
    }, [recipients]);

    // console.log(formModel,' form model')
    // console.log(userDisciplines, 'userDisciplines')
    // console.log(groupsSelected, 'groupsSelected')

    useEffect(() => {
        let talent_user_ids = [];
        if (groupsSelected.length) {
            groupsSelected.map(group => {
                let talent = [...sharableUsers]
                if (TB.find(sharableUsers, 'options')) {
                    talent = TB.flatten(TB.map(sharableUsers, 'options'))
                }

                const matchedGroup = TB.filter(talent, [group, true]);

                talent_user_ids = [...talent_user_ids, ...matchedGroup];
            })
            talent_user_ids = TB.uniq(talent_user_ids, 'id');
        }
        setFormModel({...formModel, talent_user_ids});
    }, [groupsSelected])


    const onChangeTalent = e => {
        let { name } = e.target;
        if (name === 'is_interested' && project.isBYO()) {
            name = 'has_confirmed_payout'
        }
        setGroupsSelected(TB.xor(groupsSelected, [name]))
    }

    const toggleFilters = sharableUsers.filter(item => {
        return groupsSelected.find(group => item[group])
    });

    const emailRegex = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

    let handleSubmit = async () => {
        if (!shareFormRef.current) {
            return false
        }

        let invalid = shareFormRef.current.forceValidate();

        if (invalid) {
            return false;
        }

        let {
            recipient_ids = [],
            recipient_emails = [],
            talent_user_ids = [],
            ...submitProps
        } = {...formModel}

        talent_user_ids = TB.map(talent_user_ids, 'id');
        recipient_ids = TB.map(recipient_ids, 'value');
        recipient_emails = TB.map(recipient_emails, 'value');

        let errors = [];

        if (!talent_user_ids.length) {
            errors.push('Must add at least one talent profile');
        }

        if (!recipient_emails.length && !recipient_ids.length) {
            errors.push('Must add at least one recipient');
        }

        if (!formModel.intro_message.trim().length) {
            errors.push('Message cannot be empty');
        }

        const talentOptions = [
            formModel.include_bio_match_score,
            formModel.include_linkedin,
            formModel.include_resume,
            formModel.include_profile_link,
        ];

        if (!talentOptions.some(val => !!val)) {
            return
        }

        const checkForValidCustomEmails = recipient_emails.filter(email => {
            return !emailRegex.test(email);
        })

        if (checkForValidCustomEmails.length) {
            errors.push('Invalid custom recipient email');
        }

        if (errors.length) {
            return TB.toast(errors.join('\r\n'), {theme: "danger"});
        }

        let newFormModel = {...submitProps, talent_user_ids};
        if (recipient_ids.length) {
            newFormModel.recipient_ids = recipient_ids;
        }
        if (recipient_emails.length) {
            newFormModel.recipient_emails = recipient_emails;
        }

        props.onSubmit(newFormModel);
    }

    const FormFields = useMemo(() =>{
        return [{
            style: {flexDirection: 'row'},
            className: 'share-top-row',
            fields: {
                talent_selection: {
                    style: {flexDirection: 'column'},
                    columns: 4,
                    fields: {
                        group_label: (
                            <p className="share-step-label"><span>1.</span><span>Choose Talent to Include in Email</span></p>
                        ),
                        group_selection: (
                            <div className="flex flex-col my-1">
                                <div className="flex-row flex justify-start items-center">
                                    <p className="talent-options-label">
                                        <strong>Select All:</strong>
                                    </p>
                                    <div className="talent-options flex-row flex-wrap flex items-center justify-start">
                                        {toggleOptions.map(item => {
                                            return (
                                                <div className="my-1" key={item.label} style={{paddingLeft: 3}}>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={groupsSelected.includes(item.value)}
                                                        name={item.value}
                                                        disabled={!item.count}
                                                        id={item.value}
                                                        onChange={onChangeTalent}
                                                    />
                                                    <label className="form-check-label" htmlFor={item.value}>
                                                        {item.label}&nbsp;<b><small
                                                        className="text-bold">({item.count})&nbsp;</small></b>
                                                    </label>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>
                        ),
                        talent_user_ids: {
                            type: 'checkbox-group',
                            className: "share-checkbox-group",
                            required: true,
                            style: {padding: 0},
                            options: sharableUsers,
                            label: false,
                            validations: {
                                missingTalent: ({model}, value) => {
                                    return (!value?.length) ? 'Must add at least one talent to share' : true
                                }
                            },
                            displayValue: item => (
                                <React.Fragment>
                                    <p className={TB.mapClass("flex flex-row items-center", {
                                        "disabled": item.disabled
                                    })} id={`tooltip-${item.id}`}>
                                        <span>
                                            {item.firstNamePlusInitial?.()}
                                        </span>
                                        {item.is_invited && (
                                            <span className="ml-2 text-primary">
                                                Shortlisted
                                            </span>
                                        )}
                                        {item.is_interested && item.has_confirmed_payout && (
                                            <span className="ml-2 text-success-dark">
                                                Interested
                                            </span>
                                        )}
                                        {item.is_liked && (
                                            <i className="ml-2 text-success fas fa-thumbs-up"/>
                                        )}
                                        {item.is_favorite && (
                                            <i className="ml-2 fas fa-heart text-danger"/>
                                        )}
                                    </p>
                                    {item.disabled && (
                                        <InfoTooltip type="danger" target={`tooltip-${item.id}`}>
                                            {item.is_rejected ? "Talent rejected project brief" : (
                                                item.is_withdrawn && "Talent withdrew from project brief"
                                            )}
                                        </InfoTooltip>
                                    )}
                                </React.Fragment>
                            )
                        },
                        user_discipline_legend: (
                            <div className="talent-selection-legend">
                                <h3>Legend</h3>

                                <div className="legend-row">
                                    <p className="legend-label">
                                        <span className="text-primary">
                                            Shortlisted
                                        </span>
                                    </p>
                                    <p className="legend-value">
                                        Added to your shortlist
                                    </p>
                                </div>
                                <div className="legend-row">
                                    <p className="legend-label">
                                        <span className="text-success-dark">
                                            Interested
                                        </span>
                                    </p>
                                    <p className="legend-value">
                                        Submitted proposal for your project
                                    </p>
                                </div>
                                <div className="legend-row">
                                    <p className="legend-label">
                                        <i className="text-success fas fa-thumbs-up"/>
                                    </p>
                                    <p className="legend-value">
                                        Liked for this project
                                    </p>
                                </div>
                                <div className="legend-row">
                                    <p className="legend-label">
                                        <i className="fas fa-heart text-danger"/>
                                    </p>
                                    <p className="legend-value">
                                        Saved as company favorite
                                    </p>
                                </div>
                            </div>
                        )
                    }
                },

                intro_message: {
                    columns: 4,
                    className: 'intro-message-container',
                    style: {flexDirection: 'column'},
                    fields: {
                        email_label: (
                            <p className="share-step-label"><span>2.</span><span>Define Recipients and Customize Email</span></p>
                        ),
                        group_selection: {
                            component: (
                                <p className="share-input-label recipient-label">
                                    <strong>Recipients: </strong>&nbsp;&nbsp;<i>Who do you want to share with?</i>
                                </p>
                            ),
                            style: {flex: 0},
                        },
                        recipient_ids: {
                            style: {flex: 1},
                            type: 'creatable-multi-select',
                            className: "mb-3",
                            canSelectAll: true,
                            canCreate: false,
                            label: false,
                            options: suggestions,
                            required: false,
                            selectProps: {
                                formatOptionLabel: (option) => option.format || option.label,
                                isLoading: isLoadingRecipients,
                                loadingMessage: () => 'Loading members',
                                isOptionDisabled: (option) => option.disabled,
                                closeMenuOnSelect: true,
                                placeholder: project.client_project_group ? 'Add other Wripple users in your project group:' : 'Add other Wripple users in your company:'
                            },
                            validations: {
                                missingRecipients: (model, value) => {
                                    return (!model?.recipient_emails?.length && !value?.length) ? 'Must add at least one recipient' : true
                                }
                            }
                        },
                        recipient_emails: {
                            style: {flex: 1},
                            type: 'creatable-multi-select',
                            canSelectAll: false,
                            label: false,//'Add other emails:',
                            options: [],
                            required: false,
                            selectProps: {
                                components: {DropdownIndicator: null},
                                formatOptionLabel: (option) => option.format || option.label,
                                isOptionDisabled: (option) => option.disabled,
                                closeMenuOnSelect: false,
                                noOptionsMessage: ({inputValue}) => inputValue.length ? `Invalid Email: ${inputValue}` : 'Type valid email address',
                                placeholder: 'Add other emails',
                                formatCreateLabel: inputValue => `Press enter to add ${inputValue}`,
                                isValidNewOption: (inputValue) => emailRegex.test(inputValue),
                            },
                            validations: {
                                invalidCustomEmail: ({model}, value) => {
                                    let recipientEmails = TB.map(value || [], 'value')
                                    if (recipientEmails.length && TB.find(recipientEmails, email => !emailRegex.test(email))) {
                                        return 'Invalid custom recipient';
                                    }

                                    return true;
                                }
                            }
                        },
                        subject: {
                            label: 'Subject:',
                            labelClassName: 'share-input-label',
                            type: 'text',
                            max: 150,
                            placeholder: 'Feedback on Wripple Talent',
                            required: true,
                            validationErrors: {
                                isDefaultRequiredValue: 'The subject field is required'
                            }
                        },
                        intro_message_label: (
                            <p className="share-input-label">
                                Body:
                            </p>
                        ),
                        intro_message: {
                            label: false,
                            labelClassName: 'hidden',
                            placeholder: "Enter message",
                            type: 'textarea',
                            validationErrors: {
                                isDefaultRequiredValue: 'The message body field is required'
                            },
                            required: true,
                            maxLength: 3500,
                            rows: 5,
                        },

                        include_options_label: (
                            <p className="share-input-label">
                                Include with each Candidate:
                            </p>
                        ),
                        include_options: {
                            style: {padding: 0, margin: 0, flexDirection: 'row', flexWrap: 'wrap', display: 'flex'},
                            fields: {
                                include_bio_match_score: {
                                    style: {padding: 0, margin: 0, flexBasis: '50%'},
                                    type: 'checkbox',
                                    label: 'Wripple Match Score',
                                    required: false
                                },
                                include_resume: {
                                    style: {padding: 0, margin: 0, flexBasis: '50%'},
                                    type: 'checkbox',
                                    label: 'Resume (attachment)',
                                    required: false
                                },
                                include_linkedin: {
                                    style: {padding: 0, margin: 0, flexBasis: '50%'},
                                    type: 'checkbox',
                                    label: 'Linkedin (link)',
                                    required: false
                                },
                                include_profile_link: {
                                    style: {padding: 0, margin: 0, flexBasis: '50%'},
                                    type: 'checkbox',
                                    label: 'Wripple profile (link)',
                                }
                            }
                        },
                        option_required: (!formModel.include_bio_match_score &&
                            !formModel.include_resume &&
                            !formModel.include_profile_link &&
                            !formModel.include_resume) ? (
                            <div className="field m-0">
                                <div className="form-group my-0 py-0 text-danger">
                                    <span className="invalid-feedback position-relative">
                                        Must select at least on talent option
                                    </span>
                                </div>
                            </div>
                        ): <div/>
                    },
                },

            }
        }]
    }, [position?.id, recipients, isLoadingRecipients, groupsSelected, toggleFilters]);


    return (
        <Form
            ref={shareFormRef}
            className="share-form"
            onChange={onChangeModel}
            fields={FormFields}
            model={formModel}
            submitButton={false}
            cancelButton={false}
            isDisabled={props.disabled}
            onSubmit={null}
        >
            <div className="row">
                <div className="col-12 md:w-4/12 mx-auto">
                    <button type="button" onClick={handleSubmit} disabled={props.disabled}
                            className="self-stretch my-3 btn btn-primary btn-block">
                        Submit
                    </button>
                </div>
            </div>

        </Form>
    )
}

const ShareModal = props => {
    const {project} = useProject();
    const [searchParams, setSearchParams] = useSearchParams();

    const [sharePosition, setSharePosition] = useState(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        data: sharableData,
        isInitialLoading: isLoading,
        error,
        refetch,
    } = useAPI(['fetchSharableForPosition', {position_id: sharePosition?.id, project_id: project?.id}], {
            enabled: !!sharePosition?.id && !!project?.id,
        }
    )

    const hasValidPosition = useMemo(() => {
        if (!project?.positions?.length) return null;
        let sharedPositionId = parseInt(searchParams.get('shareTalentForRole'));
        let position = TB.find(project?.positions, ['id', sharedPositionId])

        if (!position) {
            return false;
        }

        return position;
    }, [searchParams.get('shareTalentForRole'), project?.positions])

    useEffect(() => {
        let parseForSharePosition = () => {
            if (hasValidPosition) {
                setSharePosition(hasValidPosition)
            } else if (sharePosition) {
                setTimeout(() => {
                    setSharePosition(undefined)
                }, 650)
            }
        }
        return parseForSharePosition();
    }, [hasValidPosition]);

    const closeModal = () => {
        let params = new URLSearchParams(searchParams);
        params.delete('shareTalentForRole')
        return setSearchParams(params, {replace: true})
    }

    const handleSubmit = async (formModel) => {
        console.log(formModel, isSubmitting)
        if (isSubmitting) return;
        await setIsSubmitting(true)

        const response = await ProjectGroup.submitShare(project, sharePosition, formModel);
        console.log(response, 'response')
        // need to get correct info from response
        // returning response.success will close modal (for now)
        await setIsSubmitting(false)

        if (response?.success) {
            TB.toast('Share request has been sent!');
            return closeModal();
        }else return TB.showServerErrorToast();
    }
    const returnRouteParamsString = useMemo(() => {
        if(!searchParams) return '';
        let routeParams = new URLSearchParams(searchParams);
        routeParams.delete('shareTalentForRole');

        return routeParams.toString()
    }, [searchParams.toString()]);

    return (
        <Modal
            key={returnRouteParamsString}
            modalClass="share-talent-modal"
            isVisible={hasValidPosition}
            onCloseAction={closeModal}>

            {!!sharePosition && (
                <div className={TB.mapClass("sharable-position-container", {"is-submitting": isSubmitting})}>
                    <h3>Share talent profiles via email for:&nbsp;&nbsp;
                        <span className="text-primary">{sharePosition.title}</span>
                    </h3>

                    {isLoading && !error && (
                        <LoadingBanner/>
                    )}
                    {error && !isLoading && (
                        <ErrorBanner
                            title="Something went wrong"
                            message="Please try again or come back later"
                            onRetry={() => refetch()}/>
                    )}
                    {!isLoading && !error && sharableData?.length && (
                        <ShareForm
                            position={sharePosition}
                            users={sharableData}
                            onSubmit={handleSubmit}
                            disabled={isSubmitting}
                        />
                    )}
                    {isSubmitting && (
                        <div className="submit-loading">
                            <LoadingSpinner size="lg"/>
                        </div>
                    )}
                </div>
            )}


        </Modal>
    )
}

export default ShareModal;